// src/components/TabBar.jsx
import { useDataStore } from '../stores/dataStore'

// Icon components
const ListIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="8" y1="6" x2="21" y2="6"/>
    <line x1="8" y1="12" x2="21" y2="12"/>
    <line x1="8" y1="18" x2="21" y2="18"/>
    <line x1="3" y1="6" x2="3.01" y2="6"/>
    <line x1="3" y1="12" x2="3.01" y2="12"/>
    <line x1="3" y1="18" x2="3.01" y2="18"/>
  </svg>
)

const GridIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7"/>
    <rect x="14" y="3" width="7" height="7"/>
    <rect x="14" y="14" width="7" height="7"/>
    <rect x="3" y="14" width="7" height="7"/>
  </svg>
)

const BellIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
    <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
  </svg>
)

export default function TabBar() {
  const activeTab = useDataStore(s => s.activeTab)
  const setActiveTab = useDataStore(s => s.setActiveTab)
  const connections = useDataStore(s => s.connections)
  const alerts = useDataStore(s => s.alerts)
  const apps = useDataStore(s => s.getConnectionsByApp())
  const searchQuery = useDataStore(s => s.searchQuery)
  const setSearchQuery = useDataStore(s => s.setSearchQuery)

  const activeAlerts = alerts.filter(a => !a.dismissed)
  const criticalAlertCount = activeAlerts.filter(a => a.severity === 'critical').length

  const TABS = [
    { id: 'connections', label: 'Connections', icon: <ListIcon />, count: connections.length },
    { id: 'apps',        label: 'Apps',        icon: <GridIcon />, count: apps.length },
    {
      id: 'alerts',
      label: 'Alerts',
      icon: <BellIcon />,
      count: activeAlerts.length,
      accent: activeAlerts.length > 0,
      critical: criticalAlertCount > 0,
    },
  ]

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      padding: '0 16px',
      gap: 4,
      borderBottom: '1px solid var(--border-subtle)',
      background: 'var(--bg-surface)',
      flexShrink: 0,
      height: 44,
    }}>
      {TABS.map(tab => (
        <button
          key={tab.id}
          onClick={() => setActiveTab(tab.id)}
          title={tab.critical ? `${criticalAlertCount} critical alert${criticalAlertCount === 1 ? '' : 's'} need attention` : undefined}
          style={{
            padding: '6px 12px',
            borderRadius: 8,
            border: tab.critical ? '1px solid rgba(239, 68, 68, 0.55)' : '1px solid transparent',
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 500,
            fontFamily: 'var(--font-display)',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            position: 'relative',
            isolation: 'isolate',
            background: tab.critical
              ? 'rgba(239, 68, 68, 0.14)'
              : activeTab === tab.id
                ? tab.accent ? 'rgba(245, 166, 35, 0.12)' : 'rgba(77, 157, 224, 0.12)'
                : 'transparent',
            color: tab.critical
              ? 'var(--accent-red)'
              : activeTab === tab.id
                ? tab.accent ? 'var(--accent-amber)' : 'var(--accent-blue)'
                : 'var(--text-secondary)',
            boxShadow: tab.critical ? '0 0 0 1px rgba(239,68,68,0.08), 0 0 18px rgba(239,68,68,0.28)' : 'none',
            animation: tab.critical ? 'criticalTabPulse 1.25s ease-in-out infinite' : 'none',
            transition: 'background 0.15s, color 0.15s, border-color 0.15s, box-shadow 0.15s',
          }}
        >
          <span style={{
            display: 'flex',
            alignItems: 'center',
            animation: tab.critical ? 'criticalBellRing 1.25s ease-in-out infinite' : 'none',
            transformOrigin: '50% 0%',
          }}>
            {tab.icon}
          </span>
          {tab.label}
          {tab.count > 0 && (
            <span style={{
              fontSize: 10,
              fontFamily: 'var(--font-mono)',
              padding: '1px 5px',
              borderRadius: 100,
              background: tab.critical
                ? 'rgba(239, 68, 68, 0.22)'
                : activeTab === tab.id
                  ? tab.accent ? 'rgba(245, 166, 35, 0.2)' : 'rgba(77, 157, 224, 0.2)'
                  : 'var(--bg-elevated)',
              color: tab.critical
                ? '#ffb4b4'
                : activeTab === tab.id
                  ? tab.accent ? 'var(--accent-amber)' : 'var(--accent-blue)'
                  : 'var(--text-tertiary)',
              boxShadow: tab.critical ? '0 0 10px rgba(239,68,68,0.35)' : 'none',
            }}>
              {tab.count}
            </span>
          )}
        </button>
      ))}

      {/* Search */}
      <div style={{ marginLeft: 'auto', position: 'relative' }}>
        <input
          type="text"
          placeholder="Search apps, domains..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          style={{
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border-default)',
            borderRadius: 8,
            padding: '5px 10px 5px 28px',
            fontSize: 11,
            color: 'var(--text-primary)',
            outline: 'none',
            width: 160,
            fontFamily: 'var(--font-display)',
          }}
        />
        <span style={{
          position: 'absolute',
          left: 9,
          top: '50%',
          transform: 'translateY(-50%)',
          fontSize: 12,
          opacity: 0.4,
          pointerEvents: 'none',
        }}>
          🔍
        </span>
      </div>
    </div>
  )
}
